import React, { useState, useEffect, useCallback } from 'react';
import _, { debounce } from 'lodash';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  LayerGroup,
  Tooltip,
  Polyline,
  Polygon,
  useMapEvents,
  FeatureGroup,
  useMap,
  LayersControl,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { makeStyles } from '@mui/styles';

// Constants
import { MapActionView, MapConfig } from '../../constants/Constants';

// Redux
import { updateTargetLocationState } from '../../redux/slices/mapSlice';
import { updateIsCalibrationState } from '../../redux/slices/readerSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hook';

// Types
import { GeoPosition } from '../../types/GeoPosition';
import { Coordinates } from '../../types/Coordinates';

// Utils
import { calculateCentroid } from '../../utils/mapHelper';
import { createMarkerIcon, createPinIcon } from '../../utils/leaflet-map-utilities/leaflet-map-utilities';

// Components
import MapLegend from './components/MapLegend';

const useStyles = makeStyles(() => ({
  toggleButtonGroup: {
    height: '30px',
    padding: 2,
    backgroundColor: 'white',
  },
  toggleButton: {
    '&.MuiToggleButton-root': {
      fontSize: 'xx-small',
      whiteSpace: 'nowrap',
    },
  },
  divToggleButton: {
    position: 'absolute',
    zIndex: 400,
    justifyContent: 'center',
    marginLeft: 50,
    marginTop: 2,
  },
  nearReaderFilterGroup: {
    height: '30px',
    padding: 2,
    backgroundColor: 'white',
    marginLeft: 8,
  },
}));

export interface MapProps {
  center: LatLng;
  height?: string;
  width?: string;
  zoom?: number;
  coordinates: Coordinates[];
  isEditingEnabled?: boolean;
  showToggleButtons?: boolean | undefined;
  showToggleButtonNearReaders?: boolean;
  showToggleButtonFacilities?: boolean;
  showToggleButtonAirportGeofences?: boolean;
  showToggleButtonCalibration?: boolean;
  showSatelliteMap?: boolean;
  lockedLocation?: GeoPosition;
  aciveToggleTab?: string[] | undefined;
  hoveredImei?: string | null;
  onNearReaderHover?: (imei: string | null) => void;
  onCreated?: (e: any) => void;
  onEdited?: (e: any) => void;
  onDeleted?: (e: any) => void;
  nearReaderOpsStatus?: string | null;
  nearReaderIntendedUse?: string | null;
}

interface PolygonCoordinates extends Coordinates {
  name: string | undefined;
  facilityName: string | undefined;
  coordinates: [number, number][] | undefined;
}

const Map: React.FC<MapProps> = React.memo((props) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [toggleBtn, setToggleBtn] = useState<string[]>(() => props.aciveToggleTab ?? MapConfig.ToggleTabSettings.Default);
  const [isEditActive, setIsEditActive] = useState(false);
  const [positions, setPositions] = useState<Coordinates[]>([]);
  const [polygonMaps, setPolygonMaps] = useState<PolygonCoordinates[]>([]);
  const [currentZoomLevel, setCurrentZoomLevel] = useState<number>(MapConfig.DEFAULT_ZOOM);
  const readerState = useAppSelector((state) => state.readers);

  const handleToggleButton = (event: React.MouseEvent<HTMLElement>, tabName: string[]) => {
    setToggleBtn(tabName);
    dispatch(updateIsCalibrationState(tabName.includes(MapActionView.Calibration)));
  };

  const LocationMarker = () => {
    const map = useMapEvents({
      click(e) {
        map.locate();
        if (e.latlng && !isEditActive) {
          dispatch(
            updateTargetLocationState({
              latitude: parseFloat(e.latlng.lat.toFixed(10)),
              longitude: parseFloat(e.latlng.lng.toFixed(10)),
            } as GeoPosition),
          );
        }
      },
    });

    return null;
  };

  const selectViewTab = useCallback(
    (currentToggleBtn: string[], coordinateArray: Coordinates[]) => {
      let results = [...coordinateArray];

      if (props.showToggleButtons) {
        const excludedTabs = MapConfig.ToggleTabSettings.AllTabs.filter((tab) => !currentToggleBtn.includes(tab));
        if (excludedTabs.length > 0) {
          if (excludedTabs.includes(MapActionView.NearReader)) results = results.filter((v) => v._type !== 'NearReader');
          if (excludedTabs.includes(MapActionView.Facilities)) results = results.filter((v) => v._type !== 'Facility');
          if (excludedTabs.includes(MapActionView.AirportGeofence)) results = results.filter((v) => v._type !== 'AirportGeofence');
          if (excludedTabs.includes(MapActionView.Calibration)) {
            results = results.filter((v) => v._type !== 'Calibration');
            results = results.filter((v) => v._type !== 'Centroid');
          }
        }
      }

      // ↓ ADD: Filter NearReaders on map by opsStatus from list view
      if (props.nearReaderOpsStatus && props.nearReaderOpsStatus !== 'all') {
        const opsMap: Record<string, number> = {
          reporting: 0,
          'not-reporting': 1,
          blacklisted: 2,
        };
        const targetOps = opsMap[props.nearReaderOpsStatus];
        results = results.map((v) => {
          if (v._type !== 'NearReader') return v;
          return v.opsStatus === targetOps ? v : { ...v, showCircleMarker: false, showMarker: false };
        });
      }

      // ↓ ADD: Filter NearReaders on map by intendedUse from list view
      if (props.nearReaderIntendedUse && props.nearReaderIntendedUse !== 'all') {
        results = results.map((v) => {
          if (v._type !== 'NearReader') return v;
          const matchesMobile = props.nearReaderIntendedUse === 'mobile' ? v.isMobile === true : v.isMobile === false;
          return matchesMobile ? v : { ...v, showCircleMarker: false, showMarker: false };
        });
      }

      createPolygons(
        _.uniqWith(
          results?.filter((v) => v.showPolygon),
          _.isEqual,
        ),
      );
      setPositions(_.uniqWith(results, _.isEqual));
    },
    [props.showToggleButtons, props.nearReaderOpsStatus, props.nearReaderIntendedUse],
  );

  const handleCalibration = () => {
    if (!toggleBtn.includes(MapActionView.Calibration)) {
      if (props.coordinates.some((v) => v.isReaderCentroid)) setCurrentZoomLevel(MapConfig.DEFAULT_ZOOM);
    }
  };

  const debouncedSelectViewTab = useCallback(
    debounce((btn: string[], coordinates: Coordinates[]) => {
      selectViewTab(btn, coordinates);
    }, 300),
    [selectViewTab],
  );

  useEffect(() => {
    if (props.coordinates) {
      debouncedSelectViewTab(toggleBtn, props.coordinates);
    }
  }, [props.coordinates, toggleBtn, props.nearReaderOpsStatus, props.nearReaderIntendedUse]);

  const createPolygons = useCallback((coordinates: Coordinates[]) => {
    const polygonPosition: PolygonCoordinates[] = [];
    coordinates.forEach((v) => {
      if (v.geofences && v.geofences.length > 0) {
        const geof = v.geofences[0];
        polygonPosition.push({
          ...v,
          name: geof.name,
          showMarker: geof.showMarker,
          facilityName: geof.facilityName,
          coordinates: geof.coordinates?.map((o: [number, number]) => [o[1], o[0]]),
        });
      }
    });
    setPolygonMaps(polygonPosition);
  }, []);

  const CustomFeatureGroup = () => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        // Attach a listener to the 'zoomend' event inside the FeatureGroup

        const handleZoomChange = () => setCurrentZoomLevel(map.getZoom());
        map.on('zoomend', handleZoomChange);
        // Clean up the listener when the FeatureGroup unmounts
        return () => {
          map.off('zoomend', handleZoomChange);
        };
      }
    }, [map]);

    return <FeatureGroup />;
  };

  // NearReader markers filtered for rendering
  const nearReaderPositions = positions.filter((v) => v.showCircleMarker && v._type === 'NearReader');

  return (
    <>
      <MapContainer
        key={props.center?.toString() + toggleBtn.includes(MapActionView.Calibration)}
        className='map'
        center={props.center}
        scrollWheelZoom={true}
        zoomControl={true}
        zoom={props.zoom || currentZoomLevel}
        attributionControl={true}
        doubleClickZoom={true}
        dragging={true}
        style={{ height: props.height || MapConfig.DEFAULT_HEIGHT, width: props.width || MapConfig.DEFAULT_WIDTH }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        {props.showSatelliteMap === true && (
          <LayersControl>
            <LayersControl.Overlay name='Satellite'>
              <TileLayer
                id='mapbox.streets'
                attribution='&copy; Esri'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              />
            </LayersControl.Overlay>
          </LayersControl>
        )}

        <div className={classes.divToggleButton} style={{ display: 'flex', alignItems: 'center' }}>
          {props.showToggleButtons && (
            <>
              <ToggleButtonGroup value={toggleBtn} onChange={handleToggleButton} aria-label='Map layers' className={classes.toggleButtonGroup}>
                {props.showToggleButtonNearReaders !== false && (
                  <ToggleButton value={MapActionView.NearReader} aria-label='near readers' className={classes.toggleButton}>
                    Near Reader
                  </ToggleButton>
                )}
                {props.showToggleButtonFacilities !== false && (
                  <ToggleButton value={MapActionView.Facilities} aria-label='facilities' className={classes.toggleButton}>
                    Facilities
                  </ToggleButton>
                )}
                {props.showToggleButtonAirportGeofences !== false && (
                  <ToggleButton value={MapActionView.AirportGeofence} aria-label='airport geofence' className={classes.toggleButton}>
                    Airport Geofence
                  </ToggleButton>
                )}
                {props.showToggleButtonCalibration !== false && (
                  <ToggleButton
                    value={MapActionView.Calibration}
                    aria-label='calibration'
                    className={classes.toggleButton}
                    onClick={handleCalibration}
                  >
                    Calibration
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </>
          )}
        </div>

        <LayerGroup>
          {/* AirportGeofence polygons */}
          {polygonMaps
            .filter((v) => !v.editable && v.coordinates && v.coordinates.length > 0 && v._type === 'AirportGeofence')
            .map((geof, index) => (
              <Polygon key={index} positions={geof.coordinates!} pathOptions={{ color: geof.color ?? MapConfig.DEFAULT_COLOR }}>
                {geof.facilityName && !readerState.isEditLockedLocation && <Popup>{geof.facilityName}</Popup>}
              </Polygon>
            ))}

          {/* CentroidCircles */}
          {positions
            .filter((v) => v.showCircleMarker && v._type === 'CentroidCircle')
            .map((marker, index) => (
              <div key={index}>
                <Circle
                  key={`ctrcirc_${index}`}
                  center={marker.latLng}
                  pathOptions={{ color: marker.color, fillColor: marker.fillColor, dashArray: marker.dashArray }}
                  radius={marker.circleRadius || MapConfig.DEFAULT_CIRCLE_RADIUS}
                  lineCap={marker.lineCap || undefined}
                  fillOpacity={marker.fillOpacity || 0}
                >
                  {props.lockedLocation && (
                    <Marker key={`marker_${index}`} position={marker.latLng} icon={createMarkerIcon({ iconUrl: marker.markerIconUrl })}>
                      {marker.markerName && <Popup>{marker.markerName}</Popup>}
                      {marker.tooltip && <Tooltip sticky>{marker.tooltip}</Tooltip>}
                    </Marker>
                  )}
                  {marker.markerName && !isEditActive && !readerState.isEditLockedLocation && <Popup>{marker.markerName}</Popup>}
                </Circle>
              </div>
            ))}

          {/* NearReader circles + markers with hover cross-highlight */}
          {nearReaderPositions.map((marker, index) => {
            const isHighlighted = !!(props.hoveredImei && marker.imei && marker.imei === props.hoveredImei);
            const hoverHandlers =
              props.onNearReaderHover && marker.imei
                ? {
                    mouseover: () => props.onNearReaderHover!(marker.imei!),
                    mouseout: () => props.onNearReaderHover!(null),
                  }
                : undefined;

            return (
              <React.Fragment key={`nr_${index}`}>
                {/* Pin icon marker */}
                <Marker
                  position={marker.latLng}
                  icon={createPinIcon({ className: isHighlighted ? '' : 'greyscale' })}
                  alt='Reader'
                  zIndexOffset={isHighlighted ? 1000 : 0}
                  eventHandlers={hoverHandlers}
                >
                  {marker.imei && (
                    <Tooltip sticky direction='top'>
                      {marker.imei}
                    </Tooltip>
                  )}
                </Marker>

                {/* Dashed circle around reader */}
                <Circle
                  center={marker.latLng}
                  pathOptions={{
                    color: isHighlighted ? '#FFD600' : (marker.color ?? 'gray'),
                    fillColor: isHighlighted ? '#FFF9C4' : (marker.fillColor ?? 'transparent'),
                    dashArray: isHighlighted ? undefined : marker.dashArray,
                    weight: isHighlighted ? 4 : 2,
                    fillOpacity: isHighlighted ? 0.3 : (marker.fillOpacity ?? 0),
                  }}
                  radius={marker.circleRadius || MapConfig.DEFAULT_CIRCLE_RADIUS}
                  lineCap={marker.lineCap || undefined}
                  eventHandlers={hoverHandlers}
                >
                  {marker.imei && !isEditActive && !readerState.isEditLockedLocation && <Popup>{marker.imei}</Popup>}
                </Circle>
              </React.Fragment>
            );
          })}

          {/* Non-NearReader, non-CentroidCircle positions with showCircleMarker */}
          {positions
            .filter((v) => v.showCircleMarker && v._type !== 'CentroidCircle' && v._type !== 'NearReader')
            .map((marker, index) => (
              <div key={`other_${index}`}>
                <Circle
                  center={marker.latLng}
                  pathOptions={{
                    color: marker.color,
                    fillColor: marker.fillColor,
                    dashArray: marker.dashArray,
                    weight: marker.weight,
                  }}
                  radius={marker.circleRadius || MapConfig.DEFAULT_CIRCLE_RADIUS}
                  lineCap={marker.lineCap || undefined}
                  fillOpacity={marker.fillOpacity || 0}
                >
                  {marker.markerName && !isEditActive && !readerState.isEditLockedLocation && <Popup>{marker.markerName}</Popup>}
                </Circle>
              </div>
            ))}

          {/* Positions with showMarker (non-NearReader) */}
          {positions
            .filter((v) => v.showMarker && v.markerIconUrl && v._type !== 'NearReader')
            .map((marker, index) => (
              <Marker
                key={`mkr_${index}`}
                position={new LatLng(marker.latLng.lat, marker.latLng.lng)}
                icon={createMarkerIcon({ iconUrl: marker.markerIconUrl })}
              />
            ))}

          {/* Non-editable non-airport polygons */}
          {polygonMaps
            .filter((v) => !v.editable && v.coordinates && v.coordinates.length > 0 && v._type !== 'AirportGeofence')
            .map((geof, index) => (
              <Polygon key={index} positions={geof.coordinates!} pathOptions={{ color: geof.color ?? MapConfig.DEFAULT_COLOR }}>
                {geof.facilityName && !readerState.isEditLockedLocation && <Popup>{geof.facilityName}</Popup>}
              </Polygon>
            ))}

          <FeatureGroup>
            {props.isEditingEnabled && (
              <EditControl
                position='topright'
                onCreated={props.onCreated}
                onEdited={props.onEdited}
                onDeleted={props.onDeleted}
                onDrawStart={() => setIsEditActive(true)}
                onEditStart={() => setIsEditActive(true)}
                onDeleteStart={() => setIsEditActive(true)}
                onDrawStop={() => setIsEditActive(false)}
                onEditStop={() => setIsEditActive(false)}
                onDeleteStop={() => setIsEditActive(false)}
                edit={{ allowIntersection: false, remove: true, edit: true, poly: { allowIntersection: false } }}
                draw={{
                  polygon: MapConfig.EDITCONTROL_POLYGON && {
                    allowIntersection: false,
                    showLength: true,
                    feet: false,
                    drawError: { color: 'red', timeout: 2000, message: "<strong>Oh snap!</strong> you can't draw that!" },
                  },
                  rectangle: MapConfig.EDITCONTROL_RECTANGLE,
                  polyline: MapConfig.EDITCONTROL_POLYLINE,
                  circle: MapConfig.EDITCONTROL_CLICLE,
                  marker: MapConfig.EDITCONTROL_MARKER,
                  circlemarker: MapConfig.EDITCONTROL_CIRCLEMARKER,
                }}
              />
            )}
            {polygonMaps
              .filter((x) => x.editable && x.coordinates && x.coordinates.length > 0)
              .map((geofence, index) => {
                const centroidFacility = calculateCentroid(geofence.coordinates!);
                return (
                  <React.Fragment key={`poly_${index}`}>
                    <Polygon
                      positions={geofence.coordinates!}
                      pathOptions={{
                        color: isEditActive ? '#ff9800' : (geofence.color ?? MapConfig.DEFAULT_COLOR),
                        weight: isEditActive ? 5 : 2,
                        fillOpacity: isEditActive ? 0.35 : 0.15,
                        dashArray: isEditActive ? '8 6' : undefined,
                      }}
                    >
                      {' '}
                      {geofence.facilityName && !readerState.isEditLockedLocation && <Popup>{geofence.facilityName}</Popup>}
                    </Polygon>
                    {centroidFacility && geofence.showMarker && (
                      <Marker
                        position={new LatLng(centroidFacility.lng, centroidFacility.lat)}
                        icon={createMarkerIcon({ iconUrl: geofence.markerIconUrl })}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </FeatureGroup>

          {positions.filter((v) => v.showPolyline).length > 0 && (
            <Polyline positions={positions.filter((v) => v.showPolyline).map((c) => c.latLng)} color='red' />
          )}
        </LayerGroup>

        <LocationMarker />
        <CustomFeatureGroup />
        <div style={{ position: 'absolute', bottom: 0, left: 0 }}>
          <MapLegend />
        </div>
      </MapContainer>
    </>
  );
});

export default Map;
